import {
	faChevronDown,
	faChevronRight,
	faPlus,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import type {
	FilterConfig,
	FilterOption,
	FilterRule,
	PermissionConditionNode,
} from '@supply-mover-2-0/core';
import { FilterRegistry } from '@supply-mover-2-0/core';
import React, { useMemo, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { BaseFilter } from '../../../components/filters/BaseFilter';
import { Button } from '../../../components/forms/Button';
import { Checkbox } from '../../../components/forms/Checkbox';
import { Input } from '../../../components/forms/Input';
import { Label } from '../../../components/forms/Label';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { DragHandle } from '../shared/DraggableList';
import { PermissionConditionBuilder } from './PermissionConditionBuilder';

/**
 * Normalize a FilterRule.defaultValue (which may be a string, scalar, or
 * already-shaped FilterOption[]) into the FilterOption[] BaseFilter expects.
 * Mirrors the relaxed shape FilterRulesService.toFilterOption supports so
 * defaults authored in code or saved through the admin UI both round-trip.
 */
function normalizeDefaultToOptions(
	value: FilterRule['defaultValue'],
): Array<FilterOption> {
	if (value == null) return [];
	const wrap = (v: unknown): FilterOption => {
		if (typeof v === 'object' && v != null) {
			const obj = v as Record<string, unknown>;
			if (obj.id != null && 'label' in obj) {
				return v as FilterOption;
			}
			const id =
				obj.id != null
					? (obj.id as string | number)
					: obj.value != null
						? String(obj.value)
						: JSON.stringify(v);
			const label =
				typeof obj.label === 'string'
					? obj.label
					: typeof obj.name === 'string'
						? obj.name
						: obj.id != null
							? String(obj.id)
							: '';
			return { id, label, data: v };
		}
		return { id: String(v), label: String(v) };
	};
	if (Array.isArray(value)) {
		return value.map(wrap);
	}
	return [wrap(value)];
}

/**
 * Convert the BaseFilter onChange payload (FilterOption[]) back into the
 * persisted shape on FilterRule.defaultValue. Single-value filters store
 * a scalar; multi-value filters store the array. Empty selection clears.
 */
function optionsToDefaultValue(
	values: Array<FilterOption>,
	config: FilterConfig | undefined,
): FilterRule['defaultValue'] | undefined {
	if (values.length === 0) return undefined;

	const isMultiple =
		config?.multiple === true ||
		config?.type === 'multi-select' ||
		config?.type === 'date-range' ||
		config?.type === 'number-range' ||
		config?.type === 'proximity';

	if (!isMultiple) {
		const first = values[0];
		// Preserve the original data payload (e.g. date-range startDate/endDate)
		// when it exists so we don't drop information on round-trip.
		return (first.data ?? first) as FilterRule['defaultValue'];
	}

	return values.map(
		(v) => (v.data ?? { id: v.id, label: v.label }) as never,
	) as FilterRule['defaultValue'];
}

export interface FilterRuleCardProps {
	filterId: string;
	rule: FilterRule | undefined;
	/** Synthesized FilterConfig for custom-field filters (cf:*). Optional. */
	customFieldConfig?: FilterConfig;
	onChange: (next: FilterRule | undefined) => void;
	onRemove: () => void;
	disabled?: boolean;
	/**
	 * Render a drag handle in the header bar so the parent DraggableList's
	 * HandlePointerSensor can initiate a drag from this row. Default false —
	 * callers outside a DraggableList don't need it.
	 */
	showDragHandle?: boolean;
	/** Dim the card while it's being dragged (parent's renderItem signal). */
	isDragging?: boolean;
}

/**
 * Editor for a single filter's FilterRule. Renders inside an expandable
 * card — the header shows filter label + summary state (locked, has default,
 * has conditions), the body shows the editable fields.
 *
 * Default-value is captured as a JSON string for v1 because rendering inline
 * filter controls (multi-select with API endpoints, date-range pickers, etc.)
 * for every registry filter type is a separate, larger piece of work. Workd
 * users editing filter rules already understand the shape — we can graduate
 * to inline controls later without changing the persisted format.
 */
export function FilterRuleCard({
	filterId,
	rule,
	customFieldConfig,
	onChange,
	onRemove,
	disabled = false,
	showDragHandle = false,
	isDragging = false,
}: FilterRuleCardProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [expanded, setExpanded] = useState(false);
	const [advancedOpen, setAdvancedOpen] = useState(false);

	const registryEntry = FilterRegistry.getConfig(filterId) ?? customFieldConfig;
	const displayLabel = rule?.labelOverride ?? registryEntry?.label ?? filterId;

	// Convert persisted defaultValue (string | array | FilterOption[]) into
	// the FilterOption[] BaseFilter expects. useMemo so identity stays stable
	// across renders when the underlying rule.defaultValue didn't change.
	const defaultSelectedValues = useMemo(
		() => normalizeDefaultToOptions(rule?.defaultValue),
		[rule?.defaultValue],
	);

	const updateRule = (updates: Partial<FilterRule>): void => {
		const next = { ...(rule ?? {}), ...updates };
		// Drop empty rule entirely so we don't persist {} as an override.
		if (
			Object.values(next).every((v) => v == null || v === '' || v === false)
		) {
			onChange(undefined);
		} else {
			onChange(next);
		}
	};

	const handleDefaultValuesChange = (values: Array<FilterOption>): void => {
		updateRule({ defaultValue: optionsToDefaultValue(values, registryEntry) });
	};

	// Summary chips for the collapsed header
	const summary: Array<string> = [];
	if (rule?.defaultValue != null) {
		summary.push(t('default'));
	}
	if (rule?.locked === true || rule?.lockedIf != null) {
		summary.push(t('locked'));
	}
	if (rule?.boundedIf != null) {
		summary.push(t('bounded'));
	}
	if (rule?.hidden === true || rule?.hiddenIf != null) {
		summary.push(t('hidden'));
	}
	if (rule?.labelOverride != null || rule?.placeholderOverride != null) {
		summary.push(t('display'));
	}
	if (rule?.additionalValues != null && rule.additionalValues.length > 0) {
		summary.push(t(`+${String(rule.additionalValues.length)} values`));
	}

	return (
		<div
			className={cn(
				'rounded-md border',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				isDragging && 'opacity-50',
			)}
		>
			{/* Card header */}
			<div className="flex items-center gap-2 px-3 py-2">
				{showDragHandle && <DragHandle />}
				<button
					type="button"
					onClick={() => {
						setExpanded((v) => !v);
					}}
					className={cn(
						'p-1 rounded',
						colors.classes.textSecondary,
						'hover:' + colors.classes.bgSecondary,
					)}
				>
					<Icon icon={expanded ? faChevronDown : faChevronRight} size="xs" />
				</button>

				<span className={cn('text-sm font-medium', colors.classes.textPrimary)}>
					{t(displayLabel)}
				</span>

				{registryEntry?.type != null && (
					<span
						className={cn(
							'text-xs px-1.5 py-0.5 rounded',
							colors.classes.bgSecondary,
							colors.classes.textSecondary,
						)}
					>
						{t(registryEntry.type)}
					</span>
				)}

				{summary.length > 0 && (
					<span className={cn('text-xs', colors.classes.textMuted)}>
						{summary.join(' · ')}
					</span>
				)}

				<div className="flex-1" />

				<button
					type="button"
					onClick={onRemove}
					disabled={disabled}
					className={cn(
						'p-1 rounded transition-colors',
						colors.classes.textMuted,
						'hover:' + colors.classes.textError,
						disabled && 'opacity-50 cursor-not-allowed',
					)}
					title={t('Remove filter')}
				>
					<Icon icon={faTrash} size="xs" />
				</button>
			</div>

			{/* Card body */}
			{expanded && (
				<div
					className={cn(
						'px-3 pb-3 pt-1 space-y-3 border-t',
						colors.classes.borderDefault,
					)}
				>
					{/* Default value — render the real filter control inline so the
				    admin picks values exactly like an end user would. Falls back to
				    a notice if no config is resolvable yet (e.g. custom field still
				    loading). */}
					<div>
						<Label>{t('Default value')}</Label>
						{registryEntry != null ? (
							<BaseFilter
								filterType={filterId}
								config={registryEntry}
								selectedValues={defaultSelectedValues}
								onValuesChange={handleDefaultValuesChange}
								disabled={disabled}
								layout="inline"
							/>
						) : (
							<p className={cn('text-xs', colors.classes.textMuted)}>
								{t('Filter definition unavailable.')}
							</p>
						)}
						<p className={cn('text-xs mt-1', colors.classes.textMuted)}>
							{t('Pre-selected value applied when the list first loads.')}
						</p>
					</div>

					{/* Lock checkbox */}
					<Checkbox
						checked={rule?.locked === true}
						onChange={(checked) => {
							updateRule({ locked: checked || undefined });
						}}
						disabled={disabled}
						label={t('Lock filter value')}
						help={t(
							'When locked, users cannot change the value of this filter on the list.',
						)}
					/>

					{/* Display overrides */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
						<div>
							<Label htmlFor={`${filterId}-label`}>{t('Label override')}</Label>
							<Input
								id={`${filterId}-label`}
								value={rule?.labelOverride ?? ''}
								disabled={disabled}
								placeholder={registryEntry?.label ?? ''}
								onChange={(e) => {
									updateRule({
										labelOverride:
											e.target.value.length > 0 ? e.target.value : null,
									});
								}}
							/>
						</div>
						<div>
							<Label htmlFor={`${filterId}-placeholder`}>
								{t('Placeholder override')}
							</Label>
							<Input
								id={`${filterId}-placeholder`}
								value={rule?.placeholderOverride ?? ''}
								disabled={disabled}
								placeholder={registryEntry?.placeholder ?? ''}
								onChange={(e) => {
									updateRule({
										placeholderOverride:
											e.target.value.length > 0 ? e.target.value : null,
									});
								}}
							/>
						</div>
					</div>

					{/* Advanced disclosure */}
					<div>
						<button
							type="button"
							onClick={() => {
								setAdvancedOpen((v) => !v);
							}}
							className={cn(
								'flex items-center gap-1 text-xs font-semibold uppercase',
								colors.classes.textSecondary,
								'hover:' + colors.classes.textPrimary,
							)}
						>
							<Icon
								icon={advancedOpen ? faChevronDown : faChevronRight}
								size="xs"
							/>
							{t('Advanced')}
						</button>

						{advancedOpen && (
							<div className="mt-2 space-y-3">
								<ConditionField
									label={t('Apply default if')}
									help={t(
										'Apply the default value only when this condition is met.',
									)}
									value={rule?.applyDefaultIf ?? null}
									onChange={(next) => {
										updateRule({ applyDefaultIf: next });
									}}
								/>
								<ConditionField
									label={t('Lock filter if')}
									help={t(
										'Lock the filter value when this condition is met (server + UI enforced).',
									)}
									value={rule?.lockedIf ?? null}
									onChange={(next) => {
										updateRule({ lockedIf: next });
									}}
								/>
								<ConditionField
									label={t('Bound filter if')}
									help={t(
										'Restrict the filter to its default + additional values when this condition is met.',
									)}
									value={rule?.boundedIf ?? null}
									onChange={(next) => {
										updateRule({ boundedIf: next });
									}}
								/>
								<ConditionField
									label={t('Hide filter if')}
									help={t(
										'Hide the filter from the UI when this condition is met (cosmetic only).',
									)}
									value={rule?.hiddenIf ?? null}
									onChange={(next) => {
										updateRule({ hiddenIf: next });
									}}
								/>

								<AdditionalValuesEditor
									values={rule?.additionalValues ?? []}
									onChange={(next) => {
										updateRule({
											additionalValues: next.length > 0 ? next : undefined,
										});
									}}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

interface ConditionFieldProps {
	label: string;
	help: string;
	value: PermissionConditionNode | null;
	onChange: (next: PermissionConditionNode | null) => void;
}

function ConditionField({
	label,
	help,
	value,
	onChange,
}: ConditionFieldProps): React.ReactElement {
	const colors = useThemeColors();
	return (
		<div>
			<Label>{label}</Label>
			<p className={cn('text-xs mb-1', colors.classes.textMuted)}>{help}</p>
			<PermissionConditionBuilder value={value} onChange={onChange} />
		</div>
	);
}

interface AdditionalValue {
	values: Array<{ id: string | number; label: string }>;
	applyIf: PermissionConditionNode;
	applyAsDefault?: boolean;
}

interface AdditionalValuesEditorProps {
	values: Array<AdditionalValue>;
	onChange: (next: Array<AdditionalValue>) => void;
}

function AdditionalValuesEditor({
	values,
	onChange,
}: AdditionalValuesEditorProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const addRow = (): void => {
		onChange([
			...values,
			{
				values: [],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [],
				},
				applyAsDefault: true,
			},
		]);
	};

	const updateRow = (index: number, next: AdditionalValue | null): void => {
		const newValues = [...values];
		if (next == null) {
			newValues.splice(index, 1);
		} else {
			newValues[index] = next;
		}
		onChange(newValues);
	};

	return (
		<div>
			<Label>{t('Additional values')}</Label>
			<p className={cn('text-xs mb-2', colors.classes.textMuted)}>
				{t(
					'Extra filter options that appear when the condition is met (e.g. "Me", "Unassigned"). Useful for bounded filter sets.',
				)}
			</p>

			{values.length > 0 && (
				<div className="space-y-2 mb-2">
					{values.map((entry, index) => (
						<div
							key={index}
							className={cn(
								'rounded border p-2 space-y-2',
								colors.classes.borderDefault,
								colors.classes.bgSecondary,
							)}
						>
							<div>
								<Label>{t('Values (JSON)')}</Label>
								<textarea
									value={JSON.stringify(entry.values)}
									onChange={(e) => {
										try {
											const parsed = JSON.parse(e.target.value) as Array<{
												id: string | number;
												label: string;
											}>;
											updateRow(index, { ...entry, values: parsed });
										} catch {
											// Ignore invalid JSON while typing
										}
									}}
									rows={1}
									placeholder={'[{"id":"ME","label":"Me"}]'}
									className={cn(
										'w-full font-mono text-xs px-2 py-1 rounded border',
										colors.classes.borderDefault,
										colors.classes.bgPrimary,
										colors.classes.textPrimary,
									)}
								/>
							</div>

							<div>
								<Label>{t('Apply if')}</Label>
								<PermissionConditionBuilder
									value={entry.applyIf}
									onChange={(next) => {
										if (next == null) {
											updateRow(index, {
												...entry,
												applyIf: {
													type: 'group',
													joiner: 'AND',
													negated: false,
													children: [],
												},
											});
										} else {
											updateRow(index, { ...entry, applyIf: next });
										}
									}}
								/>
							</div>

							<div className="flex items-center justify-between">
								<Checkbox
									checked={entry.applyAsDefault !== false}
									onChange={(checked) => {
										updateRow(index, { ...entry, applyAsDefault: checked });
									}}
									label={t('Apply as default')}
									help={t(
										'If off, this value is only added to the bounded set (selectable, but not in the default).',
									)}
								/>

								<button
									type="button"
									onClick={() => {
										updateRow(index, null);
									}}
									className={cn(
										'p-1 rounded',
										colors.classes.textMuted,
										'hover:' + colors.classes.textError,
									)}
									title={t('Remove')}
								>
									<Icon icon={faTrash} size="xs" />
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			<Button
				variant="outline"
				size="xs"
				onClick={addRow}
				icon={<Icon icon={faPlus} size="xs" />}
			>
				{t('Add additional value')}
			</Button>
		</div>
	);
}
